import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
//updated Halimunnisa 09-06-2026
import { FilterSection, Pagination } from '../components/ui';
import { exportToExcel } from '../utils/exportUtils';

export const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  //updated Halimunnisa 09-06-2026
  // Added search and filter state to allow users to easily find specific notifications and export filtered results, enhancing usability and data management for users with large volumes of notifications.
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;

  useEffect(() => {
    setPage(1);
  }, [search, filters]);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await api.get('/notifications?limit=500'); // Increased limit to fetch more for export
        if (res.data?.success) {
          setNotifications(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifs();
  }, []);
  //updated Halimunnisa 09-06-2026
// Memoized filtered notifications to optimize performance when applying search and filters, ensuring smooth user experience even with larger datasets.
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredNotifications = useMemo(() => {
    return notifications.filter(notif => {
      // Search
      if (search && !notif.message.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      
      // Status (Read / Unread)
      if (filters.status && filters.status !== 'all') {
        if (filters.status === 'unread' && notif.read) return false;
        if (filters.status === 'read' && !notif.read) return false;
      }

      // Date Range
      const notifDate = new Date(notif.createdAt);
      
      if (filters.fromDate) {
        const [y, m, d] = filters.fromDate.split('-');
        const fromDate = new Date(y, m - 1, d, 0, 0, 0, 0);
        if (fromDate > notifDate) return false;
      }
      
      if (filters.toDate) {
        const [y, m, d] = filters.toDate.split('-');
        const toDate = new Date(y, m - 1, d, 23, 59, 59, 999);
        if (toDate < notifDate) return false;
      }

      // Quick Date
      if (filters.date && filters.date !== 'all') {
        const today = new Date();
        if (filters.date === 'today') {
          if (notifDate.toDateString() !== today.toDateString()) return false;
        } else if (filters.date === 'this_week') {
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startOfWeek.setHours(0,0,0,0);
          if (notifDate < startOfWeek) return false;
        } else if (filters.date === 'this_month') {
          if (notifDate.getMonth() !== today.getMonth() || notifDate.getFullYear() !== today.getFullYear()) return false;
        } else if (filters.date === 'this_year') {
          if (notifDate.getFullYear() !== today.getFullYear()) return false;
        }
      }

      return true;
    });
  }, [notifications, search, filters]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportData = filteredNotifications.map(notif => ({
        Message: notif.message,
        Status: notif.read ? 'Read' : 'Unread',
        Date: new Date(notif.createdAt).toLocaleString('en-IN', {
          year: 'numeric', month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true
        })
      }));
      await exportToExcel(exportData, 'Notifications_Export.xlsx');
    } catch (error) {
      console.error('Export Failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error(err);
    }
  };

  const totalPages = Math.ceil(filteredNotifications.length / limit);
  const paginatedNotifications = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredNotifications.slice(start, start + limit);
  }, [filteredNotifications, page]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col">
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500">View and manage all your notifications.</p>
        </div>
        {notifications.some(n => !n.read) && (
          <button 
            onClick={handleMarkAllAsRead}
            className="text-sm font-medium text-blum-orange hover:text-orange-600 transition-colors bg-orange-50 px-4 py-2 rounded-lg shrink-0"
          >
            Mark all as read
          </button>
        )}
      </div>
{/* updated Halimunnisa 09-06-2026
Added search and filter section to allow users to easily find specific notifications and export filtered results, enhancing usability and data management for users with large volumes of notifications. */}
      <FilterSection 
        search={search}
        setSearch={setSearch}
        filters={filters}
        setFilters={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
        statusOptions={[
          { value: 'unread', label: 'Unread' },
          { value: 'read', label: 'Read' }
        ]}
        statusPlaceholder="All Notifications"
      />
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[400px]">
        {loading ? (
          <div className="flex items-center justify-center flex-1 h-full p-12">
            <div className="w-8 h-8 border-2 border-blum-orange border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 my-auto">
            <div className="w-16 h-16 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">No notifications found</h3>
            <p className="text-sm text-gray-500 mt-1 text-center max-w-sm">
              Try adjusting your search or filters.
            </p>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-gray-100">
            {paginatedNotifications.map(notif => (
              <div 
                key={notif._id} 
                className={`p-5 transition-colors flex items-start gap-4 cursor-pointer ${!notif.read ? 'bg-orange-50/20' : 'hover:bg-gray-50'}`}
                onClick={() => {
                  if (!notif.read) handleMarkAsRead(notif._id);
                  if (notif.link) navigate(notif.link);
                }}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${!notif.read ? 'bg-orange-100 text-blum-orange' : 'bg-gray-100 text-gray-500'}`}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start gap-4">
                    <p className={`text-sm ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {notif.message}
                    </p>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(notif.createdAt).toLocaleString('en-IN', { 
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                      })}
                    </span>
                  </div>
                  {!notif.read && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notif._id); }}
                      className="text-xs text-blum-orange font-medium mt-2 hover:underline"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

